import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { isCustomerAllowedToSelectWorkshop } from '../helpers/business-rules/isCustomerAllowedToSelectWorkshop';
import { getWhatHasBeenDamaged, getWorkshopDetails } from './shared/vehicle';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';

    const getNextDamagePage = (currentPageId: string): string | undefined => {
        const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);
        const defaultPage = 'foerer';

        switch (currentPageId) {
            case 'hvad-er-skadet':
                if (damageSelections.vehicle.isMyVehicleDamaged) {
                    const isVehicleTypeOtherSelected =
                        getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other';
                    if (isVehicleTypeOtherSelected) return 'skader-andet-koeretoej';
                    return 'skadezoner';
                }
                if (damageSelections.persons.isDriverDamaged) return 'personskade';
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return defaultPage;
            case 'skader-andet-koeretoej':
            case 'skadezoner':
                if (damageSelections.persons.isDriverDamaged) return 'personskade';
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return defaultPage;
            case 'personskade':
                if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return defaultPage;
            case 'oplysninger-om-passagerer':
                if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
                return defaultPage;
        }
    };

    switch (curPageId) {
        case 'godt-at-vide':
            nextPage = 'dato';
            break;
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'koeretoej-nummerplade';
            break;
        case 'koeretoej-nummerplade':
            nextPage = 'hvilket-koeretoej';
            break;
        case 'hvilket-koeretoej':
            nextPage = 'situation';
            break;
        case 'situation':
            nextPage = 'ansvar';
            break;
        case 'ansvar':
            nextPage = 'hvad-er-skadet';
            break;
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'skadezoner':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId);
            break;
        case 'skade-ting':
            if (getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes') {
                nextPage = 'genstandsregistrering';
            } else {
                nextPage = 'foerer';
            }
            break;
        case 'genstandsregistrering':
            nextPage = 'foerer';
            break;
        case 'foerer':
            if (getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value === 'iDroveTheVehicle') {
                nextPage = 'paavirkning';
            } else {
                nextPage = 'foerer-oplysninger';
            }
            break;
        case 'foerer-oplysninger':
            nextPage = 'paavirkning';
            break;
        case 'paavirkning':
            nextPage = 'oplysninger-om-modpart';
            break;
        case 'oplysninger-om-modpart':
            if (getValueOrDefault(formConfig, formValues, 'doYouHaveInformationAboutOthersInvolved').value === 'yes') {
                nextPage = 'modpartens-rolle-1';
            } else {
                nextPage = 'dokumentation';
            }
            break;
        case 'modpartens-rolle-1':
            nextPage = 'om-modparten-1';
            break;
        case 'om-modparten-1':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson2',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-2'
                : 'dokumentation';
            break;
        case 'modpartens-rolle-2':
            nextPage = 'om-modparten-2';
            break;
        case 'om-modparten-2':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson3',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-3'
                : 'dokumentation';
            break;
        case 'modpartens-rolle-3':
            nextPage = 'om-modparten-3';
            break;
        case 'om-modparten-3':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson4',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-4'
                : 'dokumentation';
            break;
        case 'modpartens-rolle-4':
            nextPage = 'om-modparten-4';
            break;
        case 'om-modparten-4':
            nextPage = 'dokumentation';
            break;
        case 'dokumentation':
            nextPage = 'politi-og-samtykke';
            break;

        case 'politi-og-samtykke':
            if (getValueOrDefault(formConfig, formValues, 'policeContacted').value === 'Y') {
                nextPage = 'samtykke';
            } else if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cvr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'om-virksomheden';
            }
            break;
        case 'samtykke':
            if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cvr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'om-virksomheden';
            }
            break;
        case 'find-vaerksted':
            nextPage = 'om-virksomheden';
            break;
        case 'om-virksomheden':
            nextPage = 'om-dig';
            break;
        case 'om-dig':
            nextPage = 'resume';
            break;
        case 'resume':
            nextPage = undefined;
            break;
        default:
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Determines if a given 'add another person' checkbox element is selected or not.
 * @param elementId Id of the 'add another person' checkbox element
 * @param formConfig
 * @param formValues
 * @returns True if the 'add another person' checkbox is selected, else false.
 */
const isAddAnotherPersonChecked = (elementId: string, formConfig: FormConfig, formValues: FormValues): boolean => {
    const addAnotherPersonCheckbox = getValueOrDefault(formConfig, formValues, elementId);
    return addAnotherPersonCheckbox && addAnotherPersonCheckbox.length > 0
        ? addAnotherPersonCheckbox[0].value === 'yes'
        : false;
};
